import { Calendar, MapPin, PartyPopper } from 'lucide-react';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import type { Event, Holiday, Language } from '@/types';

interface CalendarBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  events: Event[];
  holidays: Holiday[];
  language: Language;
  onEventClick: (eventId: string) => void;
}

const getLocale = (language: Language): string => {
  const locales = { en: 'en-US', ru: 'ru-RU', pl: 'pl-PL' };
  return locales[language];
};

export default function CalendarBottomSheet({
  isOpen,
  onClose,
  selectedDate,
  events,
  holidays,
  language,
  onEventClick,
}: CalendarBottomSheetProps) {
  if (!isOpen || !selectedDate) return null;

  const formattedDate = selectedDate.toLocaleDateString(getLocale(language), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const hasContent = events.length > 0 || holidays.length > 0;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.bottomSheet} onClick={(e) => e.stopPropagation()}>
        {/* Drag Handle */}
        <div style={styles.dragHandle} />

        {/* Date Header */}
        <div style={styles.header}>
          <span style={styles.dateText}>{formattedDate}</span>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Holidays Section */}
          {holidays.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <PartyPopper size={18} color={Colors.accent} />
                <h3 style={styles.sectionTitle}>
                  {getTranslation('holidays', language)}
                </h3>
              </div>
              {holidays.map((holiday) => (
                <div key={holiday.date + holiday.name} style={styles.holidayItem}>
                  <div style={styles.holidayName}>{holiday.localName}</div>
                  <div style={styles.holidayEnglishName}>{holiday.name}</div>
                </div>
              ))}
            </div>
          )}

          {/* Events Section */}
          {events.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <Calendar size={18} color={Colors.primary} />
                <h3 style={styles.sectionTitle}>
                  {getTranslation('events', language)}
                </h3>
              </div>
              {events.map((event) => (
                <button
                  key={event.id}
                  onClick={() => onEventClick(event.id)}
                  style={styles.eventItem}
                >
                  {event.imageUrl && (
                    <img
                      src={event.imageUrl}
                      alt={event.title}
                      style={styles.eventImage}
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <div style={styles.eventContent}>
                    <div style={styles.eventTitle}>{event.title}</div>
                    {event.location && (
                      <div style={styles.eventLocation}>
                        <MapPin size={12} color={Colors.textSecondary} />
                        <span>{event.location}</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!hasContent && (
            <div style={styles.emptyState}>
              <Calendar size={48} color={Colors.border} />
              <p style={styles.emptyText}>
                {getTranslation('noResults', language)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 1000,
  } as React.CSSProperties,

  bottomSheet: {
    backgroundColor: Colors.surface,
    borderRadius: '20px 20px 0 0',
    padding: '12px 20px 20px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '70vh',
    overflowY: 'auto' as const,
    paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
  } as React.CSSProperties,

  dragHandle: {
    width: '40px',
    height: '4px',
    backgroundColor: Colors.border,
    borderRadius: '2px',
    margin: '0 auto 16px',
  } as React.CSSProperties,

  header: {
    marginBottom: '20px',
  } as React.CSSProperties,

  dateText: {
    fontSize: '18px',
    fontWeight: '700',
    color: Colors.text,
    textTransform: 'capitalize' as const,
  } as React.CSSProperties,

  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  } as React.CSSProperties,

  section: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  } as React.CSSProperties,

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: Colors.textSecondary,
    margin: 0,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  } as React.CSSProperties,

  holidayItem: {
    backgroundColor: Colors.background,
    borderRadius: '12px',
    padding: '12px 16px',
    borderLeft: `4px solid ${Colors.accent}`,
  } as React.CSSProperties,

  holidayName: {
    fontSize: '16px',
    fontWeight: '600',
    color: Colors.text,
    marginBottom: '2px',
  } as React.CSSProperties,

  holidayEnglishName: {
    fontSize: '14px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  eventItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: Colors.background,
    borderRadius: '12px',
    padding: '12px',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left' as const,
    borderLeft: `4px solid ${Colors.primary}`,
  } as React.CSSProperties,

  eventImage: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    objectFit: 'cover' as const,
    flexShrink: 0,
  } as React.CSSProperties,

  eventContent: {
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,

  eventTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: Colors.text,
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  eventLocation: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    gap: '12px',
  } as React.CSSProperties,

  emptyText: {
    fontSize: '14px',
    color: Colors.textSecondary,
    margin: 0,
  } as React.CSSProperties,
};
